import * as z from "zod";
import { PaymentProviderType } from "./billing-engine-types";

/**
 * Whether a team requires a CTX billing plan. Legacy teams start `unset`
 * (not billed, not yet classified); admin migrates them to one of the others.
 */
export const contextualPlanStatusSchema = z.enum([
  "unset",
  "no_plan_required",
  "needs_plan",
]);
export type ContextualPlanStatus = z.infer<typeof contextualPlanStatusSchema>;

/**
 * The active CTX plan.
 *
 * - `usage`: volume-tiered per-session pricing with a monthly floor (the
 *   default a merchant enables).
 * - `overage`: a fixed monthly base fee that includes a session allowance,
 *   with a per-session overage rate beyond it. Every term is negotiated per
 *   team and entered manually in redo admin — there are no default values.
 *
 * Tagged union so variants can be added without migrating existing rows.
 */
export const contextualPlanSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("usage") }),
  z.object({
    type: z.literal("overage"),
    /** Fixed monthly base fee, in USD micros. */
    baseMicros: z.number().int().nonnegative(),
    /** Sessions covered by the base fee before the overage rate applies. */
    includedSessions: z.number().int().nonnegative(),
    /** Per-session price beyond `includedSessions`, in USD micros. */
    overageRateMicros: z.number().int().nonnegative(),
  }),
]);
export type ContextualPlan = z.infer<typeof contextualPlanSchema>;

export const contextualBillingSettingsSchema = z.object({
  planStatus: contextualPlanStatusSchema,
  plan: contextualPlanSchema.nullish(),
  /**
   * When billing starts — the only lever for "this team isn't charged yet".
   * A free period is expressed by setting this forward, not by a second date:
   * nothing before it is metered or billed. Required for the engine to
   * bootstrap the first cycle; without it the initial-subscription setup bails
   * and the team never begins billing.
   */
  startDate: z.date().nullish(),
  /** Rail this team bills on. Absent reads as Shopify, as it does elsewhere. */
  paymentProvider: z.enum(PaymentProviderType).nullish(),
});
export type ContextualBillingSettings = z.infer<
  typeof contextualBillingSettingsSchema
>;

/**
 * Whether CTX bills this team through Shopify — i.e. the team is on a plan the
 * engine will charge, on the Shopify rail. Shared because two callers must
 * agree: the merchant banner that prompts for billing acceptance, and
 * `getSubscribedProducts`, which decides whether the Shopify subscription is
 * still needed. If those drifted apart, cancelling an unrelated product would
 * tear down the subscription CTX is billing against.
 *
 * Reads the fields directly rather than parsing the whole settings object, so
 * a date that survived serialization as a string can't turn this false.
 */
export function isCtxBilledThroughShopify(
  contextualBilling:
    | Pick<ContextualBillingSettings, "planStatus" | "plan" | "paymentProvider">
    | null
    | undefined,
): boolean {
  return (
    contextualBilling?.planStatus === "needs_plan" &&
    !!contextualBilling.plan &&
    (contextualBilling.paymentProvider ?? PaymentProviderType.SHOPIFY) ===
      PaymentProviderType.SHOPIFY
  );
}
